/**
 * Phase 19 — Payment Lifecycle State Transitions & Action Gating
 * Enforces legal state machine progressions and role-based action availability for Treasury.
 */

import { PaymentStatus, PaymentRecord } from '@/types/domain/payment-management';
import { Permission } from '@/types/domain';

export const VALID_PAYMENT_TRANSITIONS: Record<PaymentStatus, PaymentStatus[]> = {
  NOT_READY: ['READY_FOR_PAYMENT', 'CANCELLED'],
  READY_FOR_PAYMENT: ['PENDING_APPROVAL', 'APPROVED', 'SCHEDULED', 'PROCESSING', 'ON_HOLD', 'CANCELLED'],
  PENDING_APPROVAL: ['APPROVED', 'ON_HOLD', 'CANCELLED'],
  APPROVED: ['SCHEDULED', 'PROCESSING', 'ON_HOLD', 'CANCELLED'],
  SCHEDULED: ['PROCESSING', 'ON_HOLD', 'CANCELLED'],
  PROCESSING: ['PAID', 'PARTIALLY_PAID', 'FAILED', 'UNKNOWN', 'RECONCILIATION_REQUIRED'],
  PARTIALLY_PAID: ['PROCESSING', 'SCHEDULED', 'PAID', 'ON_HOLD'],
  PAID: ['RECONCILIATION_REQUIRED'], // Completed payment cannot be cancelled
  FAILED: ['RETRY_PENDING', 'CANCELLED'],
  RETRY_PENDING: ['PROCESSING', 'CANCELLED'],
  ON_HOLD: ['READY_FOR_PAYMENT', 'SCHEDULED', 'PROCESSING', 'CANCELLED'],
  RECONCILIATION_REQUIRED: ['PAID', 'PARTIALLY_PAID', 'FAILED'],
  UNKNOWN: ['RECONCILIATION_REQUIRED', 'FAILED'],
  CANCELLED: [],
};

export const TERMINAL_PAYMENT_STATUSES: PaymentStatus[] = ['PAID', 'CANCELLED'];

export function canTransitionPaymentStatus(from: PaymentStatus, to: PaymentStatus): boolean {
  if (from === to) return true;
  const allowed = VALID_PAYMENT_TRANSITIONS[from] || [];
  return allowed.includes(to);
}

export type PaymentActionType =
  | 'EXECUTE_PAYMENT'
  | 'APPROVE_PAYMENT'
  | 'SCHEDULE_PAYMENT'
  | 'RETRY_PAYMENT'
  | 'RECONCILE_PAYMENT'
  | 'PLACE_ON_HOLD'
  | 'RELEASE_HOLD'
  | 'SYNC_SAP_F110'
  | 'CANCEL_PAYMENT';

export interface PaymentActionAvailability {
  action: PaymentActionType;
  label: string;
  isAvailable: boolean;
  reason?: string;
  isDestructive?: boolean;
}

export function getAvailablePaymentActions(
  payment: PaymentRecord,
  userPermissions: { can?: (perm: Permission) => boolean; role?: string } = {}
): PaymentActionAvailability[] {
  const canProcess = userPermissions.can ? userPermissions.can('PAYMENT_PROCESS') : true;
  const canApprove = userPermissions.can ? userPermissions.can('PAYMENT_APPROVE') : true;

  return [
    {
      action: 'EXECUTE_PAYMENT',
      label: 'Execute Disbursement Now',
      isAvailable:
        (payment.status === 'READY_FOR_PAYMENT' ||
          payment.status === 'APPROVED' ||
          payment.status === 'SCHEDULED' ||
          payment.status === 'RETRY_PENDING') &&
        canProcess,
      reason: !canProcess ? 'Requires PAYMENT_PROCESS permission.' : undefined,
    },
    {
      action: 'APPROVE_PAYMENT',
      label: 'Approve Payment Release',
      isAvailable: payment.status === 'PENDING_APPROVAL' && canApprove,
      reason: payment.status !== 'PENDING_APPROVAL' ? 'Payment is not pending approval.' : undefined,
    },
    {
      action: 'RETRY_PAYMENT',
      label: 'Retry Failed Payment',
      isAvailable:
        (payment.status === 'FAILED' || payment.status === 'RETRY_PENDING') &&
        payment.retryCount < payment.maxRetries &&
        canProcess,
      reason:
        payment.retryCount >= payment.maxRetries
          ? 'Maximum retry attempts reached.'
          : payment.status !== 'FAILED' && payment.status !== 'RETRY_PENDING'
          ? 'Payment has not failed.'
          : undefined,
    },
    {
      action: 'RECONCILE_PAYMENT',
      label: 'Reconcile Bank Record',
      isAvailable:
        payment.reconciliationStatus === 'MISMATCH' ||
        payment.reconciliationStatus === 'RECONCILIATION_REQUIRED' ||
        payment.status === 'RECONCILIATION_REQUIRED',
    },
    {
      action: 'PLACE_ON_HOLD',
      label: 'Place Payment on Hold',
      isAvailable:
        payment.status !== 'ON_HOLD' &&
        payment.status !== 'PAID' &&
        payment.status !== 'CANCELLED' &&
        canProcess,
    },
    {
      action: 'RELEASE_HOLD',
      label: 'Release Hold',
      isAvailable: payment.status === 'ON_HOLD' && canProcess,
    },
    {
      action: 'SYNC_SAP_F110',
      label: 'Post SAP F110 Run',
      isAvailable: payment.sapSyncStatus !== 'SYNCED' && payment.status !== 'CANCELLED',
      reason: payment.sapSyncStatus === 'SYNCED' ? 'Already synced with SAP F110.' : undefined,
    },
    {
      action: 'CANCEL_PAYMENT',
      label: 'Cancel Payment Instruction',
      isAvailable:
        (payment.status === 'SCHEDULED' ||
          payment.status === 'READY_FOR_PAYMENT' ||
          payment.status === 'FAILED') &&
        canProcess,
      isDestructive: true,
      reason: payment.status === 'PAID' ? 'Completed disbursements cannot be cancelled.' : undefined,
    },
  ];
}
